import { useState } from 'react';
import DateInput from './DateInput.jsx';

// Strategic Account form. Mirrors DistributorForm's contact card at the
// top (same Field component + class names so the record pages look
// identical suite-wide), then swaps the "Distributor Details" fieldset
// for a "Strategic Account Details" fieldset carrying the
// strategic_account_details columns: organization, territory,
// sites_influenced, last_contact, next_contact, notes.
//
// No owner picker / POS type / status here -- strategic accounts are
// firm-wide reference contacts, not an owned sales pipeline.

const EMPTY = {
  // contacts (same as DistributorForm)
  first_name: '', last_name: '', position: '',
  company: '',
  address1: '', address2: '',
  city: '', province_state: '', postal_zip: '', country: 'Canada',
  phone_main: '', phone_cell: '', phone_fax: '',
  email1: '', email2: '', email3: '',
  website: '',
  // strategic_account_details
  organization: '', territory: '', sites_influenced: '',
  last_contact: '', next_contact: '', notes: ''
};

function dateOnly(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function normalizeInitial(initial) {
  if (!initial) return { ...EMPTY };
  const merged = { ...EMPTY };
  for (const key of Object.keys(EMPTY)) {
    if (initial[key] != null) merged[key] = initial[key];
  }
  for (const k of ['last_contact', 'next_contact']) {
    merged[k] = dateOnly(merged[k]);
  }
  return merged;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function validate(form) {
  const errors = {};
  if (!form.last_name.trim() && !form.company.trim()) {
    errors.last_name = 'Last name or company is required.';
  }
  for (const k of ['email1', 'email2', 'email3']) {
    if (form[k] && !EMAIL_RE.test(form[k])) errors[k] = 'Invalid email format.';
  }
  if (form.sites_influenced && !/^\d+$/.test(String(form.sites_influenced))) {
    errors.sites_influenced = 'Must be a whole number.';
  }
  return errors;
}

export default function StrategicAccountForm({ initial, onSubmit, submitLabel = 'Save Changes', onCancel }) {
  const [form, setForm] = useState(normalizeInitial(initial));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  function update(field) {
    return (e) => {
      const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [field]: v }));
      if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError(null);
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length > 0) return;
    setSaving(true);
    try {
      await onSubmit(form);
    } catch (err) {
      setSubmitError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="contact-card-form" noValidate>
      {submitError && <p className="error">{submitError}</p>}

      {/* ── Contact Card (identical structure to DistributorForm) ──── */}
      <div className="contact-card">
        <div className="contact-card-grid">
          <Field label="First Name" width={180}>
            <input value={form.first_name} onChange={update('first_name')} />
          </Field>
          <Field label="Last Name" width={180} error={errors.last_name}>
            <input value={form.last_name} onChange={update('last_name')} aria-invalid={!!errors.last_name} />
          </Field>
          <Field label="Phone Main" width={180}>
            <input value={form.phone_main} onChange={update('phone_main')} inputMode="tel" className="cell-mono" />
          </Field>
          <Field label="Phone Cell" width={150}>
            <input value={form.phone_cell} onChange={update('phone_cell')} inputMode="tel" className="cell-mono" />
          </Field>
          <Field label="Phone Fax" width={150}>
            <input value={form.phone_fax} onChange={update('phone_fax')} inputMode="tel" className="cell-mono" />
          </Field>

          <Field label="Position" width={220}>
            <input value={form.position} onChange={update('position')} placeholder="e.g. Lead Specialist – Retail App Support" />
          </Field>
          <Field label="Company" grow>
            <input value={form.company} onChange={update('company')} />
          </Field>

          <Field label="Address 1" grow>
            <input value={form.address1} onChange={update('address1')} />
          </Field>
          <Field label="Address 2" grow>
            <input value={form.address2} onChange={update('address2')} />
          </Field>

          <Field label="City" width={150}>
            <input value={form.city} onChange={update('city')} />
          </Field>
          <Field label="Province / State" width={120}>
            <input value={form.province_state} onChange={update('province_state')} />
          </Field>
          <Field label="Postal / Zip" width={100}>
            <input value={form.postal_zip} onChange={update('postal_zip')} className="cell-mono" />
          </Field>
          <Field label="Country" width={100}>
            <input value={form.country} onChange={update('country')} />
          </Field>

          <Field label="Email 1" width={220} error={errors.email1}>
            <input type="email" value={form.email1} onChange={update('email1')} className="cell-mono" aria-invalid={!!errors.email1} />
          </Field>
          <Field label="Email 2" width={220} error={errors.email2}>
            <input type="email" value={form.email2} onChange={update('email2')} className="cell-mono" aria-invalid={!!errors.email2} />
          </Field>
          <Field label="Email 3" width={220} error={errors.email3}>
            <input type="email" value={form.email3} onChange={update('email3')} className="cell-mono" aria-invalid={!!errors.email3} />
          </Field>

          <Field label="Website" width={280}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <input value={form.website} onChange={update('website')} placeholder="https://" className="cell-mono" style={{ flex: 1 }} />
              {form.website && (
                <a
                  href={/^https?:\/\//i.test(form.website) ? form.website : `https://${form.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary btn-sm"
                  style={{ whiteSpace: 'nowrap', textDecoration: 'none' }}
                  title="Open website in a new tab"
                >
                  Visit ↗
                </a>
              )}
            </div>
          </Field>
        </div>
      </div>

      {/* ── Strategic Account Details ─────────────────────────────── */}
      <fieldset className="lead-pipeline">
        <legend>Account Details</legend>
        <div className="contact-card-grid">
          <Field label="Organization (if different)" width={240}>
            <input value={form.organization} onChange={update('organization')} placeholder="Parent org, e.g. Parkland, Irving Oil" />
          </Field>
          <Field label="Support Scope / Territory" width={220}>
            <input value={form.territory} onChange={update('territory')} placeholder="e.g. Retail App Support, Fuel" />
          </Field>
          <Field label="Sites Influenced" width={150} error={errors.sites_influenced}>
            <input type="number" min="0" value={form.sites_influenced} onChange={update('sites_influenced')} aria-invalid={!!errors.sites_influenced} />
          </Field>
          <Field label="Last Contact" width={150}>
            <DateInput value={form.last_contact} onChange={update('last_contact')} />
          </Field>
          <Field label="Next Contact" width={150}>
            <DateInput value={form.next_contact} onChange={update('next_contact')} />
          </Field>
          <Field label="Notes" grow>
            <textarea
              rows={4}
              value={form.notes}
              onChange={update('notes')}
              placeholder="Escalation path, hours, account #, what they support — anything for quick reference."
              style={{ resize: 'vertical', width: '100%' }}
            />
          </Field>
        </div>
      </fieldset>

      <div className="form-actions contact-card-actions">
        <span className="form-hint">
          Fields marked <span className="req">*</span> are required.
        </span>
        {onCancel && (
          <button type="button" className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
        )}
        <button type="submit" className="btn-primary" disabled={saving}>
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}

// Same Field component as DistributorForm / LeadForm - copied (not
// imported) so changes there don't leak here without explicit review.
function Field({ label, children, width, grow, required, error }) {
  const style = grow
    ? { flex: '1 1 200px', minWidth: 0 }
    : { flex: `0 0 ${width || 220}px`, width: width || 220, minWidth: 0 };
  return (
    <label className={`form-field${error ? ' form-field-error' : ''}`} style={style}>
      <span>
        {label}
        {required && <span className="req"> *</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
